import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import Sidebar from "../Dashboard/Sidebar";
import UploadLoader from "../../Components/UploadLoader";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  Typography,
  Paper,
  Stack,
  Divider,
  InputAdornment,
  Switch,
  FormControlLabel,
  CircularProgress,
  ThemeProvider,
  createTheme,
  CssBaseline,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ArrowBack } from "@mui/icons-material";

// --- PREMIUM LIGHT THEME ---
const lightTheme = createTheme({
  palette: {
    mode: "light",
    background: {
      default: "#F8F9FB",
      paper: "#FFFFFF",
    },
    primary: {
      main: "#111827",
    },
    secondary: {
      main: "#6366F1",
    },
    text: {
      primary: "#111827",
      secondary: "#6B7280",
    },
    divider: "#F3F4F6",
    error: {
      main: "#EF4444",
    },
    success: {
      main: "#10B981",
    },
  },
  typography: {
    fontFamily: '"Inter", "SF Pro Display", -apple-system, sans-serif',
    button: {
      textTransform: "none",
      fontWeight: 600,
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "12px",
            backgroundColor: "#FFFFFF",
            fontSize: "0.925rem",
            transition: "all 0.2s ease",
            "& fieldset": {
              borderColor: "#E5E7EB",
              borderWidth: "1.5px",
            },
            "&:hover fieldset": {
              borderColor: "#D1D5DB",
            },
            "&.Mui-focused": {
              backgroundColor: "#FFFFFF",
              boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.1)",
              "& fieldset": {
                borderColor: "#6366F1",
                borderWidth: "1.5px",
              },
            },
          },
          "& .MuiInputLabel-root": {
            fontSize: "0.875rem",
            fontWeight: 500,
            color: "#6B7280",
            "&.Mui-focused": {
              color: "#6366F1",
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
  },
});

// --- STYLED COMPONENTS ---
const RootContainer = styled(Box)({
  minHeight: "100vh",
  display: "flex",
  backgroundColor: "#F8F9FB",
});

const ContentArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  marginLeft: "18vw",
  width: "calc(100% - 18vw)",
  padding: "40px",
  transition: "all 0.3s ease",
  [theme.breakpoints.down("md")]: {
    marginLeft: 0,
    width: "100%",
    padding: "20px",
    paddingTop: "84px",
  },
}));

const SectionCard = styled(Paper)({
  padding: "32px",
  borderRadius: "16px",
  border: "1px solid #F3F4F6",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)",
  marginBottom: "24px",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.03)",
  },
});

const SectionHeader = styled(Box)({
  marginBottom: "28px",
});

const SectionTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: "1.05rem",
  color: "#111827",
  letterSpacing: "-0.01em",
  lineHeight: 1.4,
});

const SectionSubtitle = styled(Typography)({
  fontSize: "0.825rem",
  color: "#9CA3AF",
  marginTop: "4px",
  fontWeight: 400,
  lineHeight: 1.5,
});

const UploadZone = styled(Box)({
  border: "2px dashed #E5E7EB",
  borderRadius: "14px",
  padding: "40px 24px",
  textAlign: "center",
  backgroundColor: "#FAFBFC",
  cursor: "pointer",
  transition: "all 0.25s ease",
  "&:hover": {
    borderColor: "#6366F1",
    backgroundColor: "#F5F3FF",
  },
});

const PublishButton = styled(Button)({
  padding: "14px 40px",
  fontSize: "0.95rem",
  fontWeight: 700,
  borderRadius: "12px",
  backgroundColor: "#111827",
  color: "#FFFFFF",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12)",
  letterSpacing: "-0.01em",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#1F2937",
    boxShadow: "0 4px 14px rgba(17, 24, 39, 0.2)",
    transform: "translateY(-1px)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
});

const SectionDivider = styled(Divider)({
  borderColor: "#F3F4F6",
  margin: "4px 0",
});

// --- OPTION DATA ---
const statuses = ["available", "sold", "booked", "reserved"];
const fuelTypes = ["Petrol", "Diesel", "Electric", "Hybrid", "Plug-in Hybrid"];
const transmissions = ["Automatic", "Manual"];
const vehicleCategories = ["Car", "Motorcycle", "Truck", "Van", "SUV", "Bus"];
const bodyTypes = [
  "Sedan",
  "SUV",
  "Coupe",
  "Hatchback",
  "Convertible",
  "Wagon",
  "Pickup Truck",
  "Minivan",
  "Crossover",
  "Sports Car",
  "Roadster",
  "Limousine",
];
const driveTypes = ["FWD", "RWD", "AWD", "4WD", "4x4"];

function AddCar() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const location = useLocation();
  const isEditMode = Boolean(editId);
  const inventoryData = location.state?.inventory;

  const defaultFormData = {
    car_name: "",
    slug: "",
    brand: "",
    vehicle_category: "",
    body_type: "",
    drive_type: "",
    transmission: "",
    fuel_type: "",
    engine_size: "",
    power_bhp: "",
    torque_nm: "",
    driven: "",
    color: "",
    registration: "",
    price: "",
    status: "available",
    enabled: true,
    description: "",
    meta_title: "",
    meta_description: "",
    meta_blog: "",
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [videoLinks, setVideoLinks] = useState([""]);
  const [isGeneratingSeo, setIsGeneratingSeo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Prefill form when editing
  useEffect(() => {
    if (isEditMode && inventoryData) {
      const {
        id,
        created_at,
        updated_at,
        views_count,
        images: imgData,
        video_links: vidData,
        seo_description,
        ...rest
      } = inventoryData;

      setFormData({
        ...defaultFormData,
        ...rest,
        enabled: inventoryData.enabled === 1 || inventoryData.enabled === true,
        meta_blog: seo_description || "",
      });

      // Parse existing images
      try {
        const parsed =
          typeof imgData === "string" ? JSON.parse(imgData) : imgData;
        if (Array.isArray(parsed)) {
          setExistingImages(parsed);
        }
      } catch {}

      // Parse existing video links
      try {
        const parsed =
          typeof vidData === "string" ? JSON.parse(vidData) : vidData;
        if (Array.isArray(parsed) && parsed.length > 0) {
          setVideoLinks(parsed);
        }
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  const carBrandNames = require("./VehicleBrands.js");

  // Auto-generate unique slug from car_name + brand + registration (only in add mode)
  useEffect(() => {
    if (isEditMode) return;
    const { car_name, brand, registration } = formData;
    const base = [brand, car_name, registration]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-");

    if (!base) {
      setFormData((prev) => (prev.slug !== "" ? { ...prev, slug: "" } : prev));
      return;
    }

    // Append a short random suffix for uniqueness
    const suffix = Date.now().toString(36);
    const slug = `${base}-${suffix}`;
    setFormData((prev) => ({ ...prev, slug }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.car_name, formData.brand, formData.registration]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggle = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const previews = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setImages((prev) => [...prev, ...previews]);
  };

  const handleDeleteImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("index", index);
  };

  const handleDrop = (e, index) => {
    const draggedIndex = e.dataTransfer.getData("index");
    const updated = [...images];
    const draggedItem = updated[draggedIndex];
    updated.splice(draggedIndex, 1);
    updated.splice(index, 0, draggedItem);
    setImages(updated);
  };

  const handleVideoChange = (index, value) => {
    const updated = [...videoLinks];
    updated[index] = value;
    setVideoLinks(updated);
  };

  const addVideoField = () => {
    setVideoLinks((prev) => [...prev, ""]);
  };

  const removeVideoField = (index) => {
    setVideoLinks((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGenerateSeo = async () => {
    if (!formData.brand && !formData.car_name) {
      toast.error(
        "Please fill in at least the Brand and Car Name before generating.",
      );
      return;
    }

    setIsGeneratingSeo(true);
    const token = localStorage.getItem("accessToken");

    try {
      const response = await axios.post(
        "/ai/generate-seo-content",
        {
          brand: formData.brand,
          model: formData.car_name,
          variant: formData.body_type,
          year: formData.registration ? String(formData.registration) : "",
          fuelType: formData.fuel_type,
          transmission: formData.transmission,
          mileage: formData.driven ? `${formData.driven} km` : "",
          color: formData.color,
        },
        {
          headers: {
            "Content-Type": "application/json",
            auth: token,
          },
        },
      );

      const data = response.data?.data;
      if (data) {
        setFormData((prev) => ({
          ...prev,
          meta_title: data.meta_title || prev.meta_title,
          meta_description: data.meta_description || prev.meta_description,
          meta_blog: data.seo_description || prev.meta_blog,
        }));
        toast.success("SEO content generated successfully!");
      }
    } catch (error) {
      console.error("Error generating SEO content:", error);
      toast.error("Failed to generate SEO content. Please try again.");
    } finally {
      setIsGeneratingSeo(false);
    }
  };

  const handleDeleteExistingImage = (index) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("accessToken");
    setIsSubmitting(true);

    try {
      // 1. Upload new images (if any)
      let uploadedImages = [];
      if (images.length > 0) {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        const uploadData = new FormData();
        const uuid = crypto.randomUUID();
        uploadData.append(
          "path",
          "inventory/" + currentYear + "/" + currentMonth + "/" + uuid,
        );
        images.forEach((img) => {
          uploadData.append("files[]", img.file);
        });

        const uploadRes = await axios.post(
          "https://files.flywheelcars.com/api.php?action=upload_multiple",
          uploadData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );

        if (!uploadRes.data.success) {
          toast.error("Image upload failed.");
          return;
        }

        uploadedImages = uploadRes.data.uploaded.map((img, index) => ({
          url: img.url,
          alt: img.fileName,
          sort_order: existingImages.length + index + 1,
          variants: img.variants || {},
        }));
      }

      // 2. Combine existing images (kept) + newly uploaded images
      const allImages = [
        ...existingImages.map((img, index) => ({
          ...img,
          sort_order: index + 1,
        })),
        ...uploadedImages,
      ];

      // 3. Build payload
      const { meta_blog, ...restFormData } = formData;
      const payload = {
        ...restFormData,
        power_bhp: Number(formData.power_bhp) || 0,
        torque_nm: Number(formData.torque_nm) || 0,
        driven: Number(formData.driven) || 0,
        price: Number(formData.price) || 0,
        enabled: formData.enabled ? 1 : 0,
        seo_description: meta_blog,
        images: allImages,
        video_links: videoLinks.filter((link) => link.trim() !== ""),
      };

      // 4. Call add or update API
      if (isEditMode) {
        await axios.put(`/update-inventory/${editId}`, payload, {
          headers: { "Content-Type": "application/json", auth: token },
        });
        toast.success("Inventory updated successfully!");
      } else {
        await axios.post("/add-inventory", payload, {
          headers: { "Content-Type": "application/json", auth: token },
        });
        toast.success("Car added successfully!");
      }

      // Navigate to Inventory page
      navigate("/Inventory");
    } catch (error) {
      console.error(
        isEditMode ? "Error updating car:" : "Error adding car:",
        error,
      );
      toast.error(
        isEditMode
          ? "Failed to update. Please try again."
          : "Failed to add car. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <UploadLoader isUploading={isSubmitting} />
      <RootContainer>
        <Sidebar />

        <ContentArea>
          <Box component="form" onSubmit={handleSubmit}>
            {/* ── HEADER ── */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={2}
              sx={{ mb: 5 }}
            >
              <Box>
                <Button
                  onClick={() => navigate("/Inventory")}
                  startIcon={
                    <ArrowBack
                      sx={{
                        fontSize: "1rem",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  }
                  sx={{
                    mb: 1.5,
                    px: 1.5,
                    py: 0.5,
                    color: "#6B7280",
                    fontSize: "0.825rem",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: "8px",
                    letterSpacing: "-0.01em",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#F3F4F6",
                      color: "#111827",
                      "& .MuiButton-startIcon svg": {
                        transform: "translateX(-2px)",
                      },
                    },
                  }}
                >
                  Go Back
                </Button>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: "#111827",
                    letterSpacing: "-0.025em",
                    fontSize: { xs: "1.75rem", md: "2rem" },
                  }}
                >
                  {isEditMode ? "Edit Inventory" : "Add Inventory"}
                </Typography>
                <Typography
                  sx={{ color: "#9CA3AF", fontSize: "0.875rem", mt: 0.5 }}
                >
                  {isEditMode
                    ? "Update the vehicle listing details"
                    : "Fill in the details to list a new vehicle"}
                </Typography>
              </Box>
            </Stack>

            {/* ━━ SECTION 1: BASIC INFO ━━ */}
            <SectionCard>
              <SectionHeader>
                <SectionTitle>Basic Information</SectionTitle>
                <SectionSubtitle>
                  Identity and classification of the vehicle
                </SectionSubtitle>
              </SectionHeader>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    label="Brand / Make"
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                  >
                    {carBrandNames.map((b) => (
                      <MenuItem key={b} value={b}>
                        {b}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    label="Vehicle Category"
                    name="vehicle_category"
                    value={formData.vehicle_category}
                    onChange={handleChange}
                  >
                    {vehicleCategories.map((vc) => (
                      <MenuItem key={vc} value={vc}>
                        {vc}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    label="Body Type"
                    name="body_type"
                    value={formData.body_type}
                    onChange={handleChange}
                  >
                    {bodyTypes.map((bt) => (
                      <MenuItem key={bt} value={bt}>
                        {bt}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Car Name / Model"
                    name="car_name"
                    placeholder="e.g. 911 Carrera S"
                    value={formData.car_name}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="URL Slug"
                    name="slug"
                    placeholder="e.g. porsche-911-carrera-s"
                    value={formData.slug}
                    disabled
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* ━━ SECTION 2: TECHNICAL DETAILS ━━ */}
            <SectionCard>
              <SectionHeader>
                <SectionTitle>Technical Details</SectionTitle>
                <SectionSubtitle>
                  Engine, performance, and drivetrain specifications
                </SectionSubtitle>
              </SectionHeader>

              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    label="Fuel Type"
                    name="fuel_type"
                    value={formData.fuel_type}
                    onChange={handleChange}
                  >
                    {fuelTypes.map((f) => (
                      <MenuItem key={f} value={f}>
                        {f}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    label="Drive Type"
                    name="drive_type"
                    value={formData.drive_type}
                    onChange={handleChange}
                  >
                    {driveTypes.map((dt) => (
                      <MenuItem key={dt} value={dt}>
                        {dt}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    label="Transmission"
                    name="transmission"
                    value={formData.transmission}
                    onChange={handleChange}
                  >
                    {transmissions.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Engine Size"
                    name="engine_size"
                    placeholder="e.g. 3.0L V6"
                    value={formData.engine_size}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Power"
                    name="power_bhp"
                    type="number"
                    placeholder="e.g. 450"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography
                            variant="caption"
                            color="#9CA3AF"
                            fontWeight={600}
                          >
                            BHP
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    value={formData.power_bhp}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Torque"
                    name="torque_nm"
                    type="number"
                    placeholder="e.g. 530"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography
                            variant="caption"
                            color="#9CA3AF"
                            fontWeight={600}
                          >
                            Nm
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    value={formData.torque_nm}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* ━━ SECTION 3: USAGE & REGISTRATION ━━ */}
            <SectionCard>
              <SectionHeader>
                <SectionTitle>Usage &amp; Registration</SectionTitle>
                <SectionSubtitle>
                  Mileage, ownership, and registration details
                </SectionSubtitle>
              </SectionHeader>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Kilometers Driven"
                    name="driven"
                    type="number"
                    placeholder="e.g. 25000"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography
                            variant="caption"
                            color="#9CA3AF"
                            fontWeight={600}
                          >
                            km
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    value={formData.driven}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Registration Year"
                    name="registration"
                    type="number"
                    placeholder="e.g. 2022"
                    value={formData.registration}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Exterior Color"
                    name="color"
                    placeholder="e.g. Arctic White"
                    value={formData.color}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* ━━ SECTION 4: MEDIA ━━ */}
            <SectionCard>
              <SectionHeader>
                <SectionTitle>Media</SectionTitle>
                <SectionSubtitle>
                  Upload photos and add video links for the listing
                </SectionSubtitle>
              </SectionHeader>

              <Button component="label" sx={{ width: "100%", p: 0, mb: 3 }}>
                <UploadZone sx={{ width: "100%" }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "12px",
                      backgroundColor: "#F3F4F6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 12px",
                    }}
                  >
                    <Typography sx={{ fontSize: "1.25rem", color: "#6B7280" }}>
                      +
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#111827",
                      fontSize: "0.925rem",
                      mb: 0.5,
                    }}
                  >
                    Click to upload images
                  </Typography>
                  <Typography sx={{ color: "#9CA3AF", fontSize: "0.8rem" }}>
                    Supported formats: JPG, PNG — drag to reorder after upload
                  </Typography>
                  <input
                    hidden
                    type="file"
                    multiple
                    accept="image/jpg,image/png"
                    onChange={handleImageUpload}
                  />
                </UploadZone>
              </Button>

              {existingImages.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      color: "#9CA3AF",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      mb: 1.5,
                    }}
                  >
                    Current Images
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                    {existingImages.map((img, index) => (
                      <Paper
                        key={img.url}
                        elevation={0}
                        sx={{
                          width: 120,
                          height: 88,
                          position: "relative",
                          borderRadius: "10px",
                          overflow: "hidden",
                          border: "1.5px solid #F3F4F6",
                          transition: "all 0.15s ease",
                          "&:hover": {
                            borderColor: "#E5E7EB",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                          },
                        }}
                      >
                        <img
                          src={img.url}
                          alt={img.alt || ""}
                          width="100%"
                          height="100%"
                          style={{ objectFit: "cover", display: "block" }}
                        />
                        <Button
                          onClick={() => handleDeleteExistingImage(index)}
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            minWidth: "22px",
                            width: "22px",
                            height: "22px",
                            p: 0,
                            borderRadius: "6px",
                            background: "rgba(255,255,255,0.92)",
                            backdropFilter: "blur(4px)",
                            color: "#6B7280",
                            fontWeight: "bold",
                            fontSize: "10px",
                            lineHeight: 1,
                            "&:hover": { background: "#EF4444", color: "#FFF" },
                          }}
                        >
                          ✕
                        </Button>
                        {index === 0 && (
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              background: "rgba(0,0,0,0.5)",
                              backdropFilter: "blur(4px)",
                              py: 0.25,
                              textAlign: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#FFF",
                                fontWeight: 600,
                              }}
                            >
                              COVER
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    ))}
                  </Box>
                </Box>
              )}

              {images.length > 0 && (
                <Box
                  sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 4 }}
                >
                  {images.map((img, index) => (
                    <Paper
                      key={index}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => handleDrop(e, index)}
                      elevation={0}
                      sx={{
                        width: 120,
                        height: 88,
                        position: "relative",
                        cursor: "grab",
                        borderRadius: "10px",
                        overflow: "hidden",
                        border: "1.5px solid #F3F4F6",
                        transition: "all 0.15s ease",
                        "&:hover": {
                          borderColor: "#E5E7EB",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        },
                        "&:active": { cursor: "grabbing" },
                      }}
                    >
                      <img
                        src={img.preview}
                        alt=""
                        width="100%"
                        height="100%"
                        style={{ objectFit: "cover", display: "block" }}
                      />
                      <Button
                        onClick={() => handleDeleteImage(index)}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          minWidth: "22px",
                          width: "22px",
                          height: "22px",
                          p: 0,
                          borderRadius: "6px",
                          background: "rgba(255,255,255,0.92)",
                          backdropFilter: "blur(4px)",
                          color: "#6B7280",
                          fontWeight: "bold",
                          fontSize: "10px",
                          lineHeight: 1,
                          "&:hover": { background: "#EF4444", color: "#FFF" },
                        }}
                      >
                        ✕
                      </Button>
                      {index === 0 && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: "rgba(0,0,0,0.5)",
                            backdropFilter: "blur(4px)",
                            py: 0.25,
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#FFF",
                              fontWeight: 600,
                            }}
                          >
                            COVER
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}

              <SectionDivider sx={{ my: 3 }} />

              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: "#9CA3AF",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  mb: 2,
                }}
              >
                Video Links
              </Typography>
              <Stack spacing={1.5}>
                {videoLinks.map((link, index) => (
                  <Box
                    key={index}
                    sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Paste YouTube or Vimeo URL..."
                      value={link}
                      onChange={(e) => handleVideoChange(index, e.target.value)}
                    />
                    <Button
                      onClick={() => removeVideoField(index)}
                      size="small"
                      sx={{
                        color: "#EF4444",
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        minWidth: "70px",
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: "#FEF2F2",
                        },
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                ))}
              </Stack>
              <Button
                onClick={addVideoField}
                sx={{
                  color: "#6366F1",
                  fontWeight: 600,
                  fontSize: "0.825rem",
                  mt: 1.5,
                  p: 0,
                  "&:hover": {
                    background: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                + Add video link
              </Button>
            </SectionCard>

            {/* ━━ SECTION 5: DESCRIPTION & SEO ━━ */}
            <SectionCard>
              <SectionHeader>
                <SectionTitle>Description &amp; SEO</SectionTitle>
                <SectionSubtitle>
                  Vehicle description and search engine optimization
                </SectionSubtitle>
              </SectionHeader>

              <TextField
                fullWidth
                multiline
                rows={20}
                label="Vehicle Description"
                name="description"
                placeholder="Describe the vehicle's condition, features, and highlights..."
                value={formData.description}
                onChange={handleChange}
                sx={{ mb: 3 }}
              />

              <SectionDivider sx={{ mb: 3 }} />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    color: "#9CA3AF",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  Search Engine Optimization
                </Typography>
                <Button
                  onClick={handleGenerateSeo}
                  disabled={isGeneratingSeo}
                  variant="outlined"
                  size="small"
                  startIcon={
                    isGeneratingSeo ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z" />
                      </svg>
                    )
                  }
                  sx={{
                    borderColor: "#6366F1",
                    color: "#6366F1",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    borderRadius: "10px",
                    textTransform: "none",
                    px: 2,
                    py: 0.75,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    "&:hover": {
                      borderColor: "#4F46E5",
                      backgroundColor: "#EEF2FF",
                    },
                    "&.Mui-disabled": {
                      borderColor: "#C7D2FE",
                      color: "#A5B4FC",
                    },
                  }}
                >
                  {isGeneratingSeo ? "Generating..." : "Generate with AI"}
                </Button>
              </Box>
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Meta Title"
                    name="meta_title"
                    placeholder="Page title for search engines"
                    value={formData.meta_title}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    label="Meta Description"
                    name="meta_description"
                    placeholder="Short description for search results..."
                    value={formData.meta_description}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={15}
                    label="Meta Blog"
                    name="meta_blog"
                    placeholder="Blog-style SEO content..."
                    value={formData.meta_blog}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* ━━ SECTION 6: PRICING & STATUS ━━ */}
            <SectionCard>
              <SectionHeader>
                <SectionTitle>Pricing &amp; Status</SectionTitle>
                <SectionSubtitle>
                  Set the listing price and availability
                </SectionSubtitle>
              </SectionHeader>
              <Grid container spacing={2.5} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Price"
                    name="price"
                    type="number"
                    placeholder="e.g. 4500000"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: "#111827",
                              fontSize: "1rem",
                            }}
                          >
                            &#8377;
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    value={formData.price}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    label="Status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    {statuses.map((s) => (
                      <MenuItem
                        key={s}
                        value={s}
                        sx={{ textTransform: "capitalize" }}
                      >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enabled}
                        onChange={handleToggle}
                        name="enabled"
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#10B981",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor: "#10B981",
                            },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: "0.875rem",
                          color: "#374151",
                        }}
                      >
                        Listing Enabled
                      </Typography>
                    }
                    sx={{
                      ml: 0,
                      border: "1.5px solid #E5E7EB",
                      borderRadius: "12px",
                      px: 2,
                      py: 1,
                      width: "100%",
                      backgroundColor: "#FFFFFF",
                    }}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* ── BOTTOM PUBLISH ── */}
            <Box
              sx={{ display: "flex", justifyContent: "flex-end", pt: 1, pb: 4 }}
            >
              <PublishButton
                type="submit"
                variant="contained"
                disableElevation
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : isEditMode ? (
                  "Update Listing"
                ) : (
                  "Publish Listing"
                )}
              </PublishButton>
            </Box>
          </Box>
        </ContentArea>
        <ToastContainer
          position="bottom-right"
          theme="light"
          toastStyle={{
            borderRadius: "12px",
            fontSize: "0.875rem",
            fontFamily: "Inter, sans-serif",
          }}
        />
      </RootContainer>
    </ThemeProvider>
  );
}

export default AddCar;
